// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Clock, Construction, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { ApiError } from "@/api/api-error";
import { ChainPlusIcon } from "@/components/icons";
import { DateTimePicker } from "@/components/shared/date-time-picker";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { KeyChip } from "@/components/shared/key-chip";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { SubmitForApprovalDialog } from "@/features/approvals/submit-for-approval-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useFeatureFlags } from "@/lib/feature-flags";
import { formatDateTime } from "@/lib/format";
import { useSubmitAction } from "@/lib/pending-actions/use-submit-action";
import { useDateFormat } from "@/lib/preferences";
import { TIMEZONE_OPTIONS } from "@/lib/timezones";

const chainFormSchema = z.object({
  chainName: z
    .string()
    .min(3, "Must be 3-32 characters")
    .max(32, "Must be 3-32 characters")
    .regex(/^[a-z0-9_]+$/, "Only lowercase, numbers, and underscores"),
  legibleName: z.string().trim().min(3, "Must be 3-32 characters").max(32, "Must be 3-32 characters"),
  defaultTimeZone: z.string().min(1, "Required"),
});

type ChainFormValues = z.infer<typeof chainFormSchema>;

interface CreateChainDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duplicateFrom?: {
    copyFrom: string;
    defaultTimeZone: string;
  };
}

export function CreateChainDialog({ open, onOpenChange, duplicateFrom }: CreateChainDialogProps) {
  const { t } = useTranslation("back-office");
  const [dateFormat] = useDateFormat();
  const navigate = useNavigate();
  const { scheduleChange, approvalFlow } = useFeatureFlags();
  const { data: context } = useBackofficeContext();
  const { submitAction, isAdmin, isPending } = useSubmitAction();

  const [copyEnabled, setCopyEnabled] = useState(!!duplicateFrom);
  const [copyFrom, setCopyFrom] = useState(duplicateFrom?.copyFrom ?? "");
  const [scheduledFor, setScheduledFor] = useState<string | undefined>();
  const [draftScheduledFor, setDraftScheduledFor] = useState<string | undefined>();
  const [schedulePopoverOpen, setSchedulePopoverOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<ChainFormValues | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<ChainFormValues>({
    resolver: zodResolver(chainFormSchema),
    defaultValues: {
      chainName: "",
      legibleName: "",
      defaultTimeZone: duplicateFrom?.defaultTimeZone ?? "",
    },
  });

  useEffect(
    function resetFormOnOpen() {
      if (!open) return;
      setCopyEnabled(!!duplicateFrom);
      setCopyFrom(duplicateFrom?.copyFrom ?? "");
      setScheduledFor(undefined);
      setFormError(null);
      form.reset({
        chainName: "",
        legibleName: "",
        defaultTimeZone: duplicateFrom?.defaultTimeZone ?? "",
      });
    },
    [open, duplicateFrom, form],
  );

  function toSnakeCase(str: string): string {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
  }

  const goesThroughApproval = !isAdmin && approvalFlow;

  async function onSubmit(values: ChainFormValues) {
    // Non-admin with approval flow enabled: open confirmation dialog instead of submitting directly
    if (goesThroughApproval) {
      setPendingValues(values);
      setApprovalDialogOpen(true);
      return;
    }

    await executeSubmit(values, scheduledFor);
  }

  async function executeSubmit(values: ChainFormValues, schedule?: string, message?: string) {
    setFormError(null);
    form.clearErrors(["chainName", "legibleName"]);
    try {
      const { outcome } = await submitAction(
        {
          type: "create-chain",
          ...values,
          copyFrom: copyEnabled ? copyFrom : undefined,
        },
        { scheduledFor: schedule, message },
      );

      if (outcome === "executed") {
        toast.success(t("chains.notifications.created", { name: values.legibleName }));
        onOpenChange(false);
        form.reset();
        navigate({ to: "/chains/$chainName", params: { chainName: values.chainName } });
      } else if (outcome === "scheduled") {
        toast.success(t("approvals.notifications.scheduled", { name: values.legibleName }));
        onOpenChange(false);
        form.reset();
      } else {
        toast.success(t("approvals.notifications.submittedForApproval", { name: values.legibleName }));
        setApprovalDialogOpen(false);
        onOpenChange(false);
        form.reset();
      }
    } catch (err) {
      // A name clash is the expected, actionable failure — anchor it to the field that
      // clashed. The backend signals it as `value_error.already_exists` (or a 409) and the
      // detail names the field, e.g. "chain.chainName:test" or "chain.legibleName:Test".
      // Anything else surfaces as an inline message so the dialog never fails silently (the
      // global API toast still fires for 422/5xx as a debug aid).
      if (err instanceof ApiError && (err.status === 409 || /exist/i.test(err.type ?? ""))) {
        const field = err.detail?.includes("legibleName") ? "legibleName" : "chainName";
        form.setError(field, {
          message: field === "legibleName" ? t("chains.form.legibleNameExists") : t("chains.form.chainNameExists"),
        });
      } else {
        setFormError(err instanceof ApiError ? err.message : t("chains.notifications.createFailed"));
      }
    }
  }

  const submitDisabled = isPending;

  function getSubmitLabel() {
    if (isPending) return t("buttons.create");
    if (goesThroughApproval) return t("buttons.submitForApproval");
    if (scheduledFor) return t("buttons.schedule");
    return t("buttons.create");
  }

  function getSubmitIcon() {
    if (isPending) return <Loader2 className="h-4 w-4 animate-spin" />;
    return <ChainPlusIcon className="h-4 w-4" />;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-create-chain"
        className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={ChainPlusIcon} />
              <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                <DialogHeader>
                  <DialogTitle>
                    {duplicateFrom ? t("chains.dialog.duplicateTitle") : t("chains.dialog.createTitle")}
                  </DialogTitle>
                </DialogHeader>

                <FormField
                  control={form.control}
                  name="legibleName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("chains.form.legibleName")} />
                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            if (!form.getFieldState("chainName").isDirty) {
                              form.setValue("chainName", toSnakeCase(e.target.value));
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="chainName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("chains.form.chainName")} />
                      <FormControl>
                        <Input {...field} className="font-mono" />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="defaultTimeZone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("chains.form.defaultTimeZone")} />
                      <FormControl>
                        <VirtualSelectPopover
                          value={field.value}
                          onChange={field.onChange}
                          options={TIMEZONE_OPTIONS}
                          placeholder={t("chains.form.selectTimezone")}
                          searchPlaceholder={t("chains.form.searchTimezones")}
                          emptyText={t("chains.form.noTimezones")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Copy settings toggle */}
                <div className="flex items-center gap-3">
                  <Switch id="copy-settings" checked={copyEnabled} onCheckedChange={setCopyEnabled} />
                  <Label htmlFor="copy-settings">
                    {t("chains.form.copySettings", "Copy settings from existing chain")}
                  </Label>
                </div>

                {copyEnabled && (
                  <VirtualSelectPopover
                    value={copyFrom}
                    onChange={setCopyFrom}
                    options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                    placeholder={t("chains.form.selectCopyFrom")}
                    searchPlaceholder={t("chains.form.searchCopyFrom")}
                    emptyText={t("chains.form.noCopyFrom")}
                  />
                )}

                {formError && <p className="text-sm text-destructive">{formError}</p>}
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              {scheduledFor && (
                <span className="mr-auto text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <Construction className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                  {t("approvals.scheduledForFooter", {
                    time: formatDateTime(scheduledFor, dateFormat),
                  })}
                </span>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset();
                  onOpenChange(false);
                }}
              >
                {t("buttons.cancel")}
              </Button>
              <div className="flex items-stretch">
                <Button
                  type="submit"
                  disabled={submitDisabled}
                  className={scheduleChange ? "rounded-r-none" : undefined}
                >
                  {getSubmitIcon()}
                  {getSubmitLabel()}
                </Button>
                {scheduleChange && (
                  <>
                    <Separator orientation="vertical" className="h-auto bg-primary/70" />
                    <Popover
                      open={schedulePopoverOpen}
                      onOpenChange={(open) => {
                        if (open) setDraftScheduledFor(scheduledFor);
                        setSchedulePopoverOpen(open);
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button type="button" disabled={isPending} className="rounded-l-none px-2">
                          <Clock className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" alignOffset={-2} sideOffset={-38} className="w-auto">
                        <div className="flex flex-col gap-3">
                          <p className="text-sm font-medium">
                            {isAdmin ? t("approvals.scheduleForLater") : t("approvals.requestedExecutionTime")}
                          </p>
                          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400 text-xs">
                            <Construction className="h-3.5 w-3.5 shrink-0" />
                            {t("common.workInProgress")}
                          </div>
                          <DateTimePicker value={draftScheduledFor} onChange={setDraftScheduledFor} />
                          <div className="flex justify-end gap-2">
                            {(draftScheduledFor || scheduledFor) && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setScheduledFor(undefined);
                                  setSchedulePopoverOpen(false);
                                }}
                              >
                                {t("approvals.clearSchedule")}
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => {
                                setScheduledFor(draftScheduledFor);
                                setSchedulePopoverOpen(false);
                              }}
                            >
                              {t("buttons.save")}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {goesThroughApproval && pendingValues && (
        <SubmitForApprovalDialog
          open={approvalDialogOpen}
          onOpenChange={(v) => {
            setApprovalDialogOpen(v);
            if (!v) setPendingValues(null);
          }}
          onSubmit={(msg) => executeSubmit(pendingValues, scheduledFor, msg)}
          isPending={isPending}
          actionLabel={t("approvals.actionTypes.create-chain")}
          scheduledFor={scheduledFor}
          summary={
            <div className="flex flex-col gap-1 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{t("chains.form.legibleName")}:</span>
                <span className="font-medium">{pendingValues.legibleName}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{t("chains.form.chainName")}:</span>
                <KeyChip label={pendingValues.chainName} variant="chain" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{t("chains.form.defaultTimeZone")}:</span>
                <span>{pendingValues.defaultTimeZone}</span>
              </div>
            </div>
          }
        />
      )}
    </Dialog>
  );
}
